import type { DurableChatTurnRegistryState } from "./durable-chat-turn-registry-types"
import type { SteerNote } from "../types/steer-types"
import type { QueuedMessageSeed } from "../utils/steer-note-settlement"
import type {
  ChatAppResponse,
  ChatSessionModel,
  DurableChatTurnEnrichedSession,
  DurableChatTurnPendingControl,
  DurableChatTurnSnapshot,
  DurableChatTurnStatus,
  DurableSteerNoteOutcome,
  ToolApproval,
} from "@/api/models"
import type { SessionState } from "@/stores/slices/chat-slice"

import {
  durableTurnErrorText,
  durableTurnErrorTranslationKey,
} from "../utils/durable-turn-error-i18n"
import { settleSteerNotesAtTurnEnd } from "../utils/steer-note-settlement"

/**
 * Stable pre-submit turn identity. Must be generated BEFORE any transport
 * (HTTP accept or legacy WS send) starts, so the same id survives the accept
 * response, every subscription frame, and a later history reload —
 * `use-chat-history.ts` already merges reloaded server messages onto local
 * ones by `turn_id` first, then by `messageId` (see its "MessageId-based
 * merge strategy" block), so stamping this id onto the eventual final
 * `ChatAppResponse.turn_id` is what makes that merge dedupe a durable turn's
 * completed message exactly once — no new merge logic is needed there.
 */
export function generateDurableChatTurnId(): string {
  // Defensive fallback for environments/TS libs where `crypto.randomUUID`
  // isn't guaranteed — matches the identical pattern (and its file-level
  // eslint-disable precedent) in use-stream-handler.ts's own turn_id stamp.
  /* eslint-disable @typescript-eslint/no-unnecessary-condition */
  return (
    globalThis.crypto?.randomUUID?.() ??
    `durable-turn-${Date.now()}-${Math.random().toString(36).slice(2)}`
  )
  /* eslint-enable @typescript-eslint/no-unnecessary-condition */
}

/**
 * Deterministic idempotency key for a control (e.g. a tool-approval
 * decision) posted against a durable turn. The same
 * {turn, request, call, decision} tuple always yields the same id, so a
 * retried POST (double-click, flaky network) attaches to the same control
 * server-side instead of minting a second one, and a response that arrives
 * late for a superseded decision can never be mistaken for a newer one.
 */
export function buildDurableChatTurnClientControlId({
  approved,
  callId,
  requestId,
  turnId,
}: {
  approved: boolean
  callId: string
  requestId: string
  turnId: string
}): string {
  return `${turnId}:${requestId}:${callId}:${approved ? "approve" : "deny"}`
}

/**
 * Map a durable turn's pending controls onto the legacy `ToolApproval` shape
 * so the existing (mode-agnostic) `ToolApprovalOverlay` can render them
 * unchanged. The durable protocol's `DurableChatTurnPendingControl` DTO is
 * intentionally minimal (no `title`/`summary`/`status` — see issue #2465
 * contract) so those display-only fields are synthesized here; a richer
 * server payload would let this drop the synthesis.
 */
export function mapDurablePendingControlsToToolApprovals(
  controls: DurableChatTurnPendingControl[],
  { nowIso }: { nowIso: string }
): ToolApproval[] {
  // `DurableChatTurnPendingControl.action` is currently a single-value union
  // ("tool_decision") — if the protocol ever grows another allowlisted
  // control kind, add an `action === "tool_decision"` filter back here.
  return controls.map((control) => ({
    request_id: control.requestId,
    call_id: control.callId,
    tool: control.tool ?? "",
    title: control.tool ?? "",
    summary: "",
    arguments: control.arguments ?? {},
    argumentsElided: control.argumentsElided ?? false,
    status: "pending" as const,
    created_at: nowIso,
    updated_at: nowIso,
  }))
}

/**
 * Merge a durable turn's final message into the session's completed
 * `messages` array exactly once: match by `turn_id` first, then by
 * `messageId`/`context.message_id`. Mirrors the priority order
 * `use-chat-history.ts` uses when reconciling server history, so a message
 * appended here and later reconciled against reloaded history never
 * duplicates and never regresses to a stale copy.
 */
export function mergeDurableChatTurnFinalMessage(
  messages: [string, ChatAppResponse][],
  interaction: [string, ChatAppResponse]
): [string, ChatAppResponse][] {
  const [, incoming] = interaction
  const incomingTurnId = incoming.turn_id
  const incomingMessageId = incoming.messageId ?? incoming.context.message_id

  const existingIndex = messages.findIndex(([, existing]) => {
    if (incomingTurnId && existing.turn_id === incomingTurnId) return true
    const existingMessageId = existing.messageId ?? existing.context.message_id
    if (incomingMessageId && existingMessageId === incomingMessageId) return true
    return false
  })

  if (existingIndex === -1) return [...messages, interaction]

  const next = [...messages]
  next[existingIndex] = interaction
  return next
}

/**
 * Merge a durable turn's best-effort session enrichment (issue #2513) onto
 * the sidebar's session list — the durable equivalent of the legacy
 * `title_state_changed` WebSocket handler's session patch
 * (`use-websocket-message-handler.ts`: `update.title` / `update.pending_title_suggestion`
 * applied via `store.setSessions(store.sessions.map(...))`).
 *
 * Every field is optional and applied independently: a partial or delayed
 * enrichment (the backend guards and time-bounds every step, see
 * `services/chat_turn_enrichment.py`) can never regress fields it didn't
 * carry. `title` is the one field with an explicit never-blank guard — the
 * backend always sends SOME string for `title` (possibly `""` if generation
 * hasn't run yet), so an empty incoming title is treated as "no update" here
 * exactly as `title_state_changed`'s `if (message.data.title_updated?.new_title)`
 * guard does, rather than overwriting an already-rendered name with nothing.
 *
 * Returns the SAME array reference when no session in the list matches
 * `enrichment.sessionId` or the enrichment carries nothing applicable, so
 * callers can cheaply skip a redundant store write (mirrors `snapshotsEqual`'s
 * no-op-preserving style elsewhere in this module family).
 */
export function applyDurableSessionEnrichmentToSessions(
  sessions: ChatSessionModel[],
  enrichment: DurableChatTurnEnrichedSession
): ChatSessionModel[] {
  const next = sessions.map((session) => {
    if (session?.id !== enrichment.sessionId) return session

    const update: Partial<NonNullable<ChatSessionModel>> = {}
    if (enrichment.title) update.title = enrichment.title
    if ("pendingTitleSuggestion" in enrichment) {
      update.pending_title_suggestion = enrichment.pendingTitleSuggestion ?? undefined
    }
    if (enrichment.iconType) update.iconType = enrichment.iconType
    if (enrichment.lastFunctionUsed) update.lastFunctionUsed = enrichment.lastFunctionUsed

    return Object.keys(update).length === 0 ? session : { ...session, ...update }
  })
  return next.some((session, index) => session !== sessions[index]) ? next : sessions
}

function buildChatAppResponseFromSnapshot({
  completed,
  failed = false,
  interrupted,
  snapshot,
  terminationReason,
  turnId,
}: {
  completed: boolean
  failed?: boolean
  interrupted?: boolean
  snapshot: DurableChatTurnSnapshot
  terminationReason?: string
  turnId: string
}): ChatAppResponse {
  // A saved answer (completed, or a stopped partial) can carry a Prism capture
  // status in `snapshot.error`. That is a neutral notice about memory, never a
  // failure of the answer itself: keep it off `error` so the answer renders
  // (#3339). A failed hosted-agent turn is persisted with `completed: true`,
  // so its explicit failure marker must keep genuine errors on the error path.
  const captureCode = snapshot.error?.code
  const memoryCaptureStatus =
    completed &&
    !failed &&
    (captureCode === "memory_capture_blocked_source" ||
      captureCode === "memory_capture_unconfirmed" ||
      captureCode === "memory_capture_pending")
      ? captureCode
      : undefined
  return {
    content: snapshot.content,
    turn_id: turnId,
    context: snapshot.context,
    session_state: null,
    completed,
    activities: snapshot.activities,
    // Client-side only marker (never sent to or read from the wire) — see
    // its doc comment on `ChatAppResponse` in api/models.ts. Every answer
    // built here comes from the durable-turn state machine, so this is
    // unconditionally true; it lets `system-message.tsx` distinguish a
    // durable terminal answer from a legacy one that could otherwise take
    // the exact same shape (issue #2513).
    durable_turn: true,
    ...(snapshot.finalMessageId ? { messageId: snapshot.finalMessageId } : {}),
    // Issue #2513 (finding 1): every backend-settled `cancelled`/`interrupted`
    // terminal carries a non-null `finalError` (the abort path always builds
    // one — see chat_turn_runner.py's `_abort()`), and the live WS
    // `turn_terminal` frame projects it onto `snapshot.error` for ANY
    // terminal status, not just genuine failures. Spreading it unconditionally
    // here would put `error` on the answer, which routes `system-message.tsx`
    // straight to the destructive `<Error>` card before any of the neutral
    // cancelled/interrupted handling below ever runs. `terminationReason` is
    // only ever passed for cancelled/interrupted (see below), so gating on
    // its absence keeps `failed` byte-identical while cancelled/interrupted
    // fall through to the neutral stopped/interrupted treatment.
    // `error_code` rides along with `error` (#2625): the durable terminal is
    // the only producer that keeps the backend's stable code, and
    // `system-message.tsx` needs it to tell a fair-use refusal — which gets
    // its own localized gate, not the destructive error card — from a genuine
    // failure. Matching on `error` text instead would be matching backend
    // prose.
    // A hosted agent's engine composes the failure message inside the
    // container, which has no locale — so a Swedish thread got "The agent's
    // model budget is exhausted". The stable `code` travels with it, so hand
    // `system-message.tsx` the locale key for the codes worth translating and
    // let it keep falling back to the engine's own words for the rest.
    // Only a settled failed/completed turn shows its error: the runner stamps
    // the error onto the snapshot BEFORE it settles, so a covering snapshot
    // read in that window carries it while the status still reads running —
    // the answer must keep rendering until the terminal status lands (#3354).
    ...(snapshot.error &&
    (snapshot.status === "failed" || snapshot.status === "completed") &&
    terminationReason === undefined &&
    memoryCaptureStatus === undefined
      ? {
          error: snapshot.error.message,
          error_id: snapshot.error.errorId,
          ...(snapshot.error.code ? { error_code: snapshot.error.code } : {}),
          ...(durableTurnErrorTranslationKey(snapshot.error.code)
            ? { translation_key: durableTurnErrorTranslationKey(snapshot.error.code) }
            : {}),
        }
      : {}),
    ...(interrupted !== undefined ? { interrupted } : {}),
    ...(terminationReason !== undefined ? { termination_reason: terminationReason } : {}),
    // A saved answer (completed, or stopped) stays saved in HAI even when
    // Prism capture was skipped or failed. Carry only these machine codes to
    // the neutral notice; never display abort prose or route the answer
    // through the destructive error card.
    ...(memoryCaptureStatus ? { memory_capture_status: memoryCaptureStatus } : {}),
  }
}

/** Canonical statuses that leave the turn's answer in `messages` (durably
 *  persisted) rather than `streamingAnswer` (a locally-held, resumable
 *  partial — mirrors how the legacy stream handler treats an aborted turn).
 *
 *  R9 (durable chat turns risk review): mirrors the backend's own
 *  `_PARTIAL_ANSWER_STATUSES` (`chat_turn_runner.py`) — `completed` always
 *  persists a row, and so does a STOPPED turn (`cancelled`/`interrupted`):
 *  `_abort()` calls `_persist_final(terminal_status=...)` for both, exactly
 *  like legacy's `save_message(..., interrupted=True)`. `failed` is the one
 *  terminal status excluded on both sides — legacy persists nothing for an
 *  errored turn either — so it is the only status left in `streamingAnswer`
 *  below rather than merged here. Getting this wrong in the other direction
 *  (as before this fix, `["completed"]` only) is a regression from legacy:
 *  legacy keeps a stopped answer visible; a durable `cancelled`/`interrupted`
 *  answer would instead vanish from the UI the moment another turn starts on
 *  the session (it only reappeared after a full reload, which re-reads it
 *  from the now-correct message history). */
const MESSAGE_PERSISTED_STATUSES = new Set<DurableChatTurnStatus>([
  "completed",
  "cancelled",
  "interrupted",
])

/** Session type of hosted-agent threads (the "agents" route's
 *  `overrideSessionType`, builtin_routes_seeder.py). */
export const HOSTED_AGENT_SESSION_TYPE = "hosted-agent"

/**
 * Whether a session's backend persists a row for a FAILED durable turn.
 *
 * Only the hosted-agent run queue does (`HostedAgentRunService.fail` keeps the
 * user's prompt and writes the reason; `_refuse_scheduled` does the same for a
 * turn refused before dispatch). For those sessions `failed` merges into
 * `messages` like the other terminals — left in `streamingAnswer`, the
 * persisted row arrived in `messages` too and the failure rendered TWICE in a
 * live tab. Merging dedupes on `turn_id` / `messageId`, so live and reloaded
 * agree.
 *
 * Every other durable session (Explorer) still persists nothing for a failed
 * turn, so it keeps the failure as a local partial in `streamingAnswer`:
 * merged into `messages` it would stay behind when Retry/Continue starts the
 * next turn, and the question would show twice.
 */
export function persistsFailedDurableTurns(sessionType: string | undefined): boolean {
  return sessionType === HOSTED_AGENT_SESSION_TYPE
}

export type DurableChatTurnSessionPatch = Partial<SessionState>

/**
 * The hydration state machine: canonical durable-turn status → chat session
 * UI patch. Applied identically whether the state arrived from a live
 * subscription frame or from a hydration read (`GET .../turns/current` or
 * `.../turns/{turnId}`) — hydration never re-derives UI state through any
 * other path, so returning to a chat can never restart execution or diverge
 * from what a live tab would show.
 *
 * Status → UI mapping:
 *  - accepted          → isLoading, isAwaitingSave; no content yet.
 *  - running           → isStreaming (unless the snapshot already reports
 *                         `responseComplete`, i.e. content is fully arrived
 *                         and only persistence is pending); isAwaitingSave.
 *  - waiting-approval  → same as running, plus `toolApprovals` populated
 *                         from the snapshot's pending controls.
 *  - completed         → final answer merged into `messages` exactly once
 *                         (by turn_id, then messageId); streaming state
 *                         cleared; nothing left to save; `canContinue: false`.
 *  - cancelled/         → the backend persists a partial-answer row for these
 *    interrupted           exactly like `completed` (see
 *                         `MESSAGE_PERSISTED_STATUSES`'s doc comment), so they
 *                         merge into `messages` too — never left to vanish
 *                         from the UI the moment another turn starts. The
 *                         merged message keeps `interrupted: true` and
 *                         `termination_reason` so `system-message.tsx`'s
 *                         stopped-indicator still renders it as stopped, not
 *                         as an ordinary completed answer, and
 *                         `canContinue: true` keeps Retry/Continue working —
 *                         mirrors the legacy locally-stopped state, which
 *                         durable must match rather than regress from.
 *  - failed            → `error` set and `canContinue: true` so
 *                         Retry/Continue keep working. Where the backend
 *                         persists a row for it (hosted-agent sessions, see
 *                         `persistsFailedDurableTurns`) the answer merges into
 *                         `messages` like the other terminals; everywhere else
 *                         it stays a locally-held partial in `streamingAnswer`,
 *                         mirroring a locally-aborted legacy turn.
 */
/**
 * Settle composer steer chips against the outcomes the runner has published.
 *
 * Returns `undefined` when nothing changes, so the patch omits `steerNotes`
 * entirely rather than rewriting the array on every emit — the mapper runs on
 * each registry update, and a fresh array identity every time would rerender the
 * composer continuously.
 *
 * Only ever moves a chip OFF `pending`: an outcome is terminal, and a stale
 * re-delivery of the same event must not resurrect a settled chip.
 */
function settleSteerNotes(
  current: SteerNote[] | undefined,
  statuses: Record<string, DurableSteerNoteOutcome> | undefined
): SteerNote[] | undefined {
  if (!current?.length || !statuses) return undefined
  // A Map, not the record itself: an index read on `Record<string, T>` is typed
  // as always-present, so `statuses[id]` would silently claim an outcome for
  // every chip. `Map.get` returns `T | undefined`, which is the truth.
  const outcomes = new Map(Object.entries(statuses))
  const settles = current.some((note) => note.status === "pending" && outcomes.has(note.steer_id))
  if (!settles) return undefined
  return current.map((note) => {
    if (note.status !== "pending") return note
    const outcome = outcomes.get(note.steer_id)
    return outcome ? { ...note, status: outcome } : note
  })
}

/**
 * Fold both steer-chip steps into one patch fragment.
 *
 * Status settling runs on every emit; the TURN-END settlement (#2469) runs
 * once the turn is terminal and is what a durable turn was missing entirely.
 * That settlement lives in the legacy request's `finally` — which a durable
 * send returns before ever reaching (see `sendDurableExplorerChatTurn`) — so
 * a note the model never saw was silently dropped at the next send instead of
 * coming back as a queued message.
 *
 * Idempotent: re-running it over already-settled chips changes nothing, so the
 * repeated terminal emits a subscription produces cannot requeue twice.
 */
function settleSteerNotesForPatch({
  current,
  statuses,
  isTerminalTurn,
  isMessageQueueEnabled,
  currentQueuedMessages,
}: {
  current: SteerNote[] | undefined
  statuses: Record<string, DurableSteerNoteOutcome> | undefined
  isTerminalTurn: boolean
  isMessageQueueEnabled: boolean
  currentQueuedMessages: QueuedMessageSeed[] | undefined
}): DurableChatTurnSessionPatch {
  const settled = settleSteerNotes(current, statuses)
  if (!isTerminalTurn) return settled ? { steerNotes: settled } : {}

  const notes = settled ?? current
  if (!notes?.length) return {}

  const settlement = settleSteerNotesAtTurnEnd(notes, isMessageQueueEnabled)
  const patch: DurableChatTurnSessionPatch = { steerNotes: settlement.steerNotes }
  if (settlement.queuedMessagesToAppend.length > 0) {
    patch.queuedMessages = [...(currentQueuedMessages ?? []), ...settlement.queuedMessagesToAppend]
  }
  return patch
}

export function mapDurableChatTurnStateToSessionPatch({
  currentMessages,
  currentSteerNotes,
  currentQueuedMessages,
  isMessageQueueEnabled = false,
  question,
  registryState,
  sessionType,
}: {
  currentMessages: [string, ChatAppResponse][]
  currentSteerNotes?: SteerNote[]
  currentQueuedMessages?: QueuedMessageSeed[]
  isMessageQueueEnabled?: boolean
  question: string
  registryState: DurableChatTurnRegistryState
  /** The session's type. Only where its backend persists a row for a failed
   *  turn (`persistsFailedDurableTurns`: hosted agents) does `failed` merge
   *  into `messages`; omitted, it behaves as Explorer. */
  sessionType?: string
}): DurableChatTurnSessionPatch {
  const { snapshot, status, turnId } = registryState
  const mergesIntoMessages =
    MESSAGE_PERSISTED_STATUSES.has(status) ||
    (status === "failed" && persistsFailedDurableTurns(sessionType))
  const nowIso = new Date().toISOString()
  const steerPatch = settleSteerNotesForPatch({
    current: currentSteerNotes,
    statuses: snapshot.steerStatuses,
    isTerminalTurn: MESSAGE_PERSISTED_STATUSES.has(status) || status === "failed",
    isMessageQueueEnabled,
    currentQueuedMessages,
  })

  if (status === "waiting-approval") {
    const toolApprovals = mapDurablePendingControlsToToolApprovals(snapshot.controls, { nowIso })
    return {
      streamingAnswer: [
        question,
        buildChatAppResponseFromSnapshot({ snapshot, turnId, completed: false }),
      ],
      isLoading: false,
      isStreaming: true,
      canContinue: false,
      isAwaitingSave: true,
      error: undefined,
      errorCode: undefined,
      toolApprovals,
      toolApprovalSessionId: toolApprovals.length > 0 ? registryState.sessionId : null,
      ...steerPatch,
    }
  }

  if (status === "accepted") {
    return {
      streamingAnswer: [
        question,
        buildChatAppResponseFromSnapshot({ snapshot, turnId, completed: false }),
      ],
      isLoading: true,
      isStreaming: false,
      canContinue: false,
      isAwaitingSave: true,
      error: undefined,
      errorCode: undefined,
      toolApprovals: [],
      toolApprovalSessionId: null,
      ...steerPatch,
    }
  }

  if (status === "running") {
    return {
      streamingAnswer: [
        question,
        // Once the text is final, mark the answer completed like the legacy
        // `response_complete` handler does: the page keeps `isStreaming` on the
        // message until the save lands, so `completed` is what ends the caret
        // and the typewriter (#3482).
        buildChatAppResponseFromSnapshot({
          snapshot,
          turnId,
          completed: snapshot.responseComplete,
        }),
      ],
      isLoading: false,
      isStreaming: !snapshot.responseComplete,
      canContinue: false,
      isAwaitingSave: true,
      error: undefined,
      errorCode: undefined,
      toolApprovals: [],
      toolApprovalSessionId: null,
      ...steerPatch,
    }
  }

  if (mergesIntoMessages) {
    // `cancelled`/`interrupted` land here too now (R9) — stamp the same
    // `interrupted`/`termination_reason` markers a live subscription would
    // have shown in `streamingAnswer` onto the now-merged message, so the
    // rendered answer still reads as stopped rather than as an ordinary
    // completed one. `completed` (no marker) keeps `canContinue: false`;
    // the stopped statuses keep `canContinue: true` so Retry/Continue stays
    // available, matching the legacy locally-stopped convention.
    const isCancelledLike = status === "cancelled" || status === "interrupted"
    // `failed` keeps `terminationReason` undefined so the snapshot's error
    // (and its locale key) lands on the answer and `system-message.tsx`
    // renders the failure card — the cancelled/interrupted statuses take the
    // neutral stopped treatment instead.
    const isFailed = status === "failed"
    const finalResponse = buildChatAppResponseFromSnapshot({
      snapshot,
      turnId,
      completed: true,
      failed: isFailed,
      interrupted: isCancelledLike ? true : undefined,
      terminationReason: isCancelledLike ? status : undefined,
    })
    return {
      messages: mergeDurableChatTurnFinalMessage(currentMessages, [question, finalResponse]),
      streamingAnswer: null,
      isLoading: false,
      isStreaming: false,
      canContinue: isCancelledLike || isFailed,
      isAwaitingSave: false,
      // The turn is over, so a requested cancel is no longer pending — clear it
      // alongside the other transient turn flags above. Stop alone never needed
      // this (nothing waits on it), but stop-and-redirect does: the auto-send
      // effect refuses to fire while `cancelPending` is set, so leaving it true
      // stranded the redirect forever (issue #2813).
      cancelPending: false,
      // The agent workspace renders the session's plain `error` string into
      // its own card (`agent-chat-view.tsx` → `Chat.Error`), which never
      // passes through a component that could translate it — so it has to be
      // the reader's language here, not the container's English.
      error: isFailed
        ? durableTurnErrorText(snapshot.error) || "Durable chat turn failed"
        : undefined,
      errorCode: isFailed ? snapshot.error?.code : undefined,
      toolApprovals: [],
      toolApprovalSessionId: null,
      ...steerPatch,
    }
  }

  // failed, where the backend persists no row for it (every session but a
  // hosted agent's) — the answer stays a locally-held partial, mirroring a
  // locally-aborted legacy turn, so Retry/Continue replace it rather than
  // leaving the failed question behind in `messages`.
  const partialResponse = buildChatAppResponseFromSnapshot({ snapshot, turnId, completed: false })

  return {
    streamingAnswer: [question, partialResponse],
    isLoading: false,
    isStreaming: false,
    canContinue: true,
    isAwaitingSave: false,
    // Terminal too — same reasoning as the persisted branch above (#2813).
    cancelPending: false,
    // The agent workspace renders this string straight into its error card, so
    // it has to be the reader's language, not the container's English.
    error: durableTurnErrorText(snapshot.error) || "Durable chat turn failed",
    errorCode: snapshot.error?.code,
    toolApprovals: [],
    toolApprovalSessionId: null,
    ...steerPatch,
  }
}
